import { computed, inject, Service, signal } from '@angular/core';
import {
  hasRole,
  isMgsAbrechnungUser,
  isMgsAdmin,
  isMgsArzt,
  isMgsBuchungszentraleUser,
  isMgsKidsTerminUser,
  isMgsSpowiUser,
  IUser
} from '@mng/mgs-lib';
import { MngSessionHandlerService, MngUserService } from '@mng/mng-auth';
import { UserContextData } from '@mng/domain';

export interface IAuthState {
  user?: IUser;
}

const defaultState: IAuthState = {
  user: undefined
};

export const sessionExpiredInfoText = `Sie wurden abgemeldet da Sie längere Zeit nicht in der MGS Applikation aktiv waren. Bitte melden Sie sich erneut an.`;

@Service()
export class AuthStateFacade {
  readonly sessionHandlerService = inject(MngSessionHandlerService);
  private readonly userService = inject(MngUserService);
  authState = signal<IAuthState>(defaultState);

  user = computed(() => this.authState().user);

  authenticated = computed(() => !!this.user());
  userRoles = computed(() => this.user()?.authorities ?? []);

  isAdmin = computed(() => this.user() && isMgsAdmin(this.user().authorities));
  isArzt = computed(() => this.user() && isMgsArzt(this.user().authorities));
  isAbrechnungUser = computed(() => this.user() && isMgsAbrechnungUser(this.user().authorities));
  isSpowiUser = computed(() => this.user() && isMgsSpowiUser(this.user().authorities));
  isBuchungszentraleUser = computed(() => this.user() && isMgsBuchungszentraleUser(this.user().authorities));
  isKidsTerminUser = computed(() => this.user() && isMgsKidsTerminUser(this.user().authorities));

  updateUser(user: IUser) {
    this.authState.set({ user });

    this.sessionHandlerService.stopSessionHandling();
    if (user) {
      const userContextData: UserContextData = {
        userName: user.name,
        email: user.email,
        appName: 'MGS'
      };
      this.userService.setUserContextData(userContextData);

      const cookieExpiredCheckHandler = (cookieValue: string) => {
        const expirationTime = new Date(cookieValue);
        const now = new Date();
        const result = now.getTime() > expirationTime.getTime();
        // console.log('cookieExpiredCheckHandler expirationTime: ', expirationTime, 'now', now, 'result', result);
        return result;
      };

      this.sessionHandlerService.startSessionHandling({
        cookieName: 'mgs_token_expiration',
        dialogInfoText: sessionExpiredInfoText,
        forceStart: true,
        cookieCheckHandler: cookieExpiredCheckHandler
      });
    } else {
      this.userService.setUserContextData(undefined);
    }
  }

  resetUser() {
    this.updateUser(undefined);
  }

  hasRole(role: string): boolean {
    return hasRole(role, this.userRoles());
  }
}
